import type * as XLSXType from 'xlsx';
import IPoll from '../model/IPoll';
import { IPollResultsResponse } from '../services/DataService';
import { getQuestionTypeLabel } from './pollLabels';
import { fileTimestamp, sanitizeFileName } from './format';

type XLSXModule = typeof XLSXType;

function applySheetFormatting(XLSX: XLSXModule, worksheet: XLSXType.WorkSheet, columnWidths: number[], headerColumnsCount?: number): void {
    worksheet['!cols'] = columnWidths.map((wch) => ({ wch }));
    if (!headerColumnsCount) return;

    const range = XLSX.utils.decode_range(worksheet['!ref'] ?? 'A1:A1');
    const toCol = XLSX.utils.encode_col(Math.max(0, headerColumnsCount - 1));
    worksheet['!autofilter'] = { ref: `A1:${toCol}${Math.max(1, range.e.r + 1)}` };
    (worksheet as Record<string, unknown>)['!freeze'] = {
        xSplit: 0,
        ySplit: 1,
        topLeftCell: 'A2',
        activePane: 'bottomLeft',
        state: 'frozen',
    };
}

function applyNumericFormat(XLSX: XLSXModule, worksheet: XLSXType.WorkSheet, rowStart: number, rowEnd: number, column: number, format: string): void {
    for (let row = rowStart; row <= rowEnd; row += 1) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
        if (cell && typeof cell.v === 'number') cell.z = format;
    }
}

export function buildResultsWorkbook(XLSX: XLSXModule, poll: IPoll, data: IPollResultsResponse): XLSXType.WorkBook {
    const workbook = XLSX.utils.book_new();
    type Row = Array<string | number>;

    const summaryRows: Row[] = [
        ['Опрос', poll.Title ?? ''],
        ['ID опроса', poll.Id ?? ''],
        ['Всего ответов', data.totalResponses ?? 0],
        ['Вопросов', data.questions?.length ?? 0],
        ['Дата выгрузки', new Date().toLocaleString('ru-RU')],
    ];

    const questionsRows: Row[] = [['№', 'ID вопроса', 'Тип', 'Вопрос', 'Ответов', 'Среднее']];
    const detailsRows: Row[] = [[
        '№ вопроса', 'ID вопроса', 'Тип', 'Вопрос', 'Категория', 'Значение', 'Количество', 'Процент', 'Вес',
    ]];

    (data.questions ?? []).forEach((question, index) => {
        const no = index + 1;
        const type = getQuestionTypeLabel(question.questionType);
        const avg = typeof question.average === 'number' ? Number(question.average.toFixed(2)) : '';

        questionsRows.push([no, question.questionId ?? '', type, question.questionText ?? '', question.totalAnswers ?? 0, avg]);

        (question.choices ?? []).forEach((choice) => {
            detailsRows.push([no, question.questionId ?? '', type, question.questionText ?? '', 'Вариант',
                choice.choiceText ?? '', choice.count ?? 0, Number(choice.percentage ?? 0) / 100, '']);
        });
        (question.textAnswers ?? []).forEach((answer) => {
            detailsRows.push([no, question.questionId ?? '', type, question.questionText ?? '', 'Текстовый ответ',
                answer ?? '', '', '', '']);
        });
        (question.words ?? []).forEach((word) => {
            detailsRows.push([no, question.questionId ?? '', type, question.questionText ?? '', 'Слово',
                word.text ?? '', word.count ?? 0, '', word.weight ?? 0]);
        });
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
    const questionsSheet = XLSX.utils.aoa_to_sheet(questionsRows);
    const detailsSheet = XLSX.utils.aoa_to_sheet(detailsRows);

    applySheetFormatting(XLSX, summarySheet, [24, 90]);
    applySheetFormatting(XLSX, questionsSheet, [6, 40, 24, 70, 12, 14], 6);
    applySheetFormatting(XLSX, detailsSheet, [10, 40, 24, 70, 18, 50, 12, 12, 10], 9);
    applyNumericFormat(XLSX, questionsSheet, 1, questionsRows.length - 1, 5, '0.00');
    applyNumericFormat(XLSX, detailsSheet, 1, detailsRows.length - 1, 7, '0.00%');

    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Сводка');
    XLSX.utils.book_append_sheet(workbook, questionsSheet, 'Вопросы');
    XLSX.utils.book_append_sheet(workbook, detailsSheet, 'Детализация');
    return workbook;
}

/**
 * Builds and saves the XLSX. The `xlsx` package is ~400 kB, so it is loaded
 * on first use instead of shipping in the main bundle.
 */
export async function downloadResultsWorkbook(poll: IPoll, data: IPollResultsResponse): Promise<void> {
    const XLSX = await import('xlsx');
    const workbook = buildResultsWorkbook(XLSX, poll, data);
    const fileName = `${sanitizeFileName(poll.Title ?? 'Результаты', 'results')}-${fileTimestamp()}.xlsx`;
    XLSX.writeFile(workbook, fileName);
}
